/**
 * Theme application for the web Control Room. The Settings "Theme" control
 * (System / Light / Dark) writes here; the choice is cached in localStorage so
 * the very first paint is correct (no flash), and "System" follows the OS and
 * reacts to OS changes live. Dark = `data-theme="dark"` on <html>; light is the
 * default (no attribute), so only [data-theme="dark"] tokens need overriding.
 */

export type ThemePref = "System" | "Light" | "Dark";

const STORAGE_KEY = "adaptr-theme";
const darkMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

function resolvePref(pref: ThemePref): "light" | "dark" {
  if (pref === "Dark") return "dark";
  if (pref === "Light") return "light";
  return darkMediaQuery.matches ? "dark" : "light";
}

function applyResolvedMode(mode: "light" | "dark"): void {
  const root = document.documentElement;
  if (mode === "dark") root.setAttribute("data-theme", "dark");
  else root.removeAttribute("data-theme");
}

// Only one OS listener at a time — installed only while the pref is "System".
let systemChangeListener: ((event: MediaQueryListEvent) => void) | null = null;

/** Apply a theme preference, persist it, and (for System) follow the OS live. */
export function applyTheme(pref: ThemePref): void {
  localStorage.setItem(STORAGE_KEY, pref);
  applyResolvedMode(resolvePref(pref));

  if (systemChangeListener) {
    darkMediaQuery.removeEventListener("change", systemChangeListener);
    systemChangeListener = null;
  }
  if (pref === "System") {
    systemChangeListener = () => applyResolvedMode(resolvePref("System"));
    darkMediaQuery.addEventListener("change", systemChangeListener);
  }
}

/** Read the cached preference and apply it. Call once at boot, before render. */
export function initTheme(): void {
  const stored = localStorage.getItem(STORAGE_KEY) as ThemePref | null;
  applyTheme(stored ?? "System");
}
